if (!customElements.get('pos-availability')) {
  customElements.define(
    'pos-availability',
    class PosAvailability extends HTMLElement {
      constructor() {
        super();

        this.hint = this.querySelector('[data-pos-hint]');
        this.trigger = this.querySelector('[data-pos-trigger]');
        this.variantChangeUnsubscriber = undefined;
      }

      connectedCallback() {
        if (this.trigger) {
          this.trigger.addEventListener('click', () => {
            const drawer = this.getDrawer();
            if (drawer) drawer.show(this.trigger);
          });
        }

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.dataset.sectionId) return;
          const variantId = event.data.variant ? event.data.variant.id : null;
          this.setVariant(variantId);
        });
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      getDrawer() {
        return document.getElementById(this.dataset.drawerId);
      }

      setVariant(variantId) {
        const hasVariant = Boolean(variantId);
        if (this.hint) this.hint.classList.toggle('hidden', hasVariant);
        if (this.trigger) this.trigger.classList.toggle('hidden', !hasVariant);

        const drawer = this.getDrawer();
        if (drawer) drawer.setVariant(variantId);
      }
    }
  );
}

if (!customElements.get('pos-availability-drawer')) {
  customElements.define(
    'pos-availability-drawer',
    class PosAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);
        this.fetchPromise = null;
        this.quantitiesByVariant = null;

        this.querySelector('.pos-availability-drawer__close').addEventListener('click', () => {
          this.hide();
        });

        // The element itself is the full-viewport overlay — clicking it (outside the inner panel) closes.
        this.addEventListener('click', (event) => {
          if (event.target === this) this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      connectedCallback() {
        if (this.moved || this.parentElement === document.body) return;
        this.moved = true;

        // Remove a stale copy left behind by a previous render (theme editor section reload).
        const stale = document.querySelector(`body > pos-availability-drawer#${this.id}`);
        if (stale && stale !== this) stale.remove();

        document.body.appendChild(this);
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (target !== this && !target.closest('pos-availability-drawer') && !target.closest('pos-availability')) {
          this.hide();
        }
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
        this.loadAvailability();
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      setVariant(variantId) {
        this.dataset.variantId = variantId || '';
        if (this.quantitiesByVariant) this.renderStatuses();
      }

      loadAvailability() {
        if (!this.fetchPromise) {
          this.setAttribute('aria-busy', 'true');
          this.fetchPromise = fetch(`${this.dataset.endpoint}?productId=${this.dataset.productId}`)
            .then((response) => {
              if (!response.ok) throw new Error(`POS availability request failed (${response.status})`);
              return response.json();
            })
            .then((json) => {
              this.quantitiesByVariant = this.parseAvailability(json);
            })
            .finally(() => {
              this.removeAttribute('aria-busy');
            });
        }

        this.fetchPromise
          .then(() => {
            this.querySelector('[data-pos-error]').classList.add('hidden');
            this.renderStatuses();
          })
          .catch((error) => {
            console.error(error);
            this.fetchPromise = null;
            this.querySelector('[data-pos-error]').classList.remove('hidden');
          });
      }

      parseAvailability(json) {
        const quantitiesByVariant = {};
        const variantEdges = json.data.product.variants.edges;

        variantEdges.forEach((variantEdge) => {
          const variantNode = variantEdge.node;
          const variantId = variantNode.id.split('/').pop();
          const locations = {};

          variantNode.inventoryItem.inventoryLevels.edges.forEach((levelEdge) => {
            const levelNode = levelEdge.node;
            const available = levelNode.quantities.find((quantity) => quantity.name === 'available');
            const locationId = String(levelNode.location.id || '').split('/').pop();
            if (locationId) locations[locationId] = available ? available.quantity : 0;
          });

          quantitiesByVariant[variantId] = locations;
        });

        return quantitiesByVariant;
      }

      // Returns the quantity for a store, matched by Shopify location ID — or
      // undefined when the location is not present in the availability response
      // (missing/misconfigured location ID setting).
      getStoreQuantity(store, locations) {
        const locationId = (store.dataset.locationId || '').trim().split('/').pop();
        if (locationId && typeof locations[locationId] !== 'undefined') {
          return locations[locationId];
        }

        return undefined;
      }

      renderStatuses() {
        const variantId = this.dataset.variantId;
        const locations = this.quantitiesByVariant ? this.quantitiesByVariant[variantId] : null;
        const lowStockThreshold = parseInt(this.dataset.lowStockThreshold, 10) || 5;

        this.querySelectorAll('[data-pos-store]').forEach((store) => {
          const statusContainer = store.querySelector('[data-pos-status]');
          statusContainer.innerHTML = '';
          if (!variantId || !locations) return;

          const quantity = this.getStoreQuantity(store, locations);

          // A location that is missing from the response is a configuration issue,
          // not an out-of-stock state — leave the status row empty instead.
          if (typeof quantity === 'undefined') {
            if (!store.missingLocationWarned) {
              store.missingLocationWarned = true;
              const storeTitle = store.querySelector('.pos-availability-store__title')?.textContent.trim();
              console.warn(
                `[pos-availability] No Shopify location matches store "${
                  storeTitle || store.dataset.locationId
                }" (location ID: "${
                  store.dataset.locationId || 'not set'
                }"). Check the "Shopify location ID" setting of the POS store block.`
              );
            }
            return;
          }

          let status = 'in_stock';
          if (quantity <= 0) {
            status = 'sold_out';
          } else if (quantity <= lowStockThreshold) {
            status = 'low_stock';
          }

          const template = this.querySelector(`[data-pos-status-template="${status}"]`);
          if (template) statusContainer.appendChild(template.content.firstElementChild.cloneNode(true));
        });
      }
    }
  );
}

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const drawer = event.target.closest('pos-availability-drawer');
    if (drawer) drawer.show();
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const drawer = event.target.closest('pos-availability-drawer');
    if (drawer) drawer.hide();
  });
}
